import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import localStorage from 'platform/utilities/storage/localStorage';

import manifest from '../manifest.json';
import {
  CLAIMS_EVIDENCE_API_NAME,
  recordUploadFailureEvent,
  recordFileUploadEvent,
  recordUploadStartEvent,
  recordUploadSuccessEvent,
} from '../utils/analytics';
import { formatUploadDateTime } from '../utils/helpers';
import {
  calcProgress,
  clearNotification,
  getAppealsV2,
  loadUploaderAfterTokenRefresh,
  refreshAccessTokenBeforeUpload,
  setNotification,
} from './index';
import {
  CLEAR_SC_ERRORS,
  DONE_UPLOADING,
  SET_PROGRESS,
  SET_UNAUTHORIZED,
  SET_UPLOAD_ERROR,
  SET_UPLOADER,
  SET_UPLOADING,
} from './types';
import { DOC_TYPE_MAP } from '../utils/supplementalClaimDocTypes';

// Multipart params for a single upload. Exported so tests can lock the wire
// contract without instantiating FineUploaderBasic.
export function buildClaimsEvidenceUploadParams(fileMeta, appealId) {
  const password = fileMeta?.password?.value;

  return {
    documentTypeId: fileMeta?.docType?.value,
    supplementalClaimId: appealId,
    ...(password ? { password } : {}),
  };
}

export const clearSCUploadErrors = () => dispatch => {
  dispatch({ type: CLEAR_SC_ERRORS });
};

// vets-api returns a DOC_UPLOAD_* code as `detail` for upload failures
// the veteran can act on, and a sentence for everything else (such as
// "documentTypeId 999 is not supported"). Only the codes reach analytics.
// See V0::ClaimsEvidenceController and ClaimsEvidence::PdfUnlocker in vets-api.
const UPLOAD_ERROR_CODE = /^DOC_UPLOAD_[A-Z_]+$/;
export const UNKNOWN_UPLOAD_ERROR = 'UNKNOWN';

export function parseUploadErrorKey(response) {
  try {
    const detail = JSON.parse(response || '{}')?.errors?.[0]?.detail;

    return UPLOAD_ERROR_CODE.test(detail) ? detail : UNKNOWN_UPLOAD_ERROR;
  } catch {
    // Non-JSON body (proxy error page, truncated response)
    return UNKNOWN_UPLOAD_ERROR;
  }
}

// AppealInfo renders the body — only it has the router context for the
// tab-switch and focus.
export function buildSupplementalClaimUploadNotification({
  fileCount,
  uploadedAt,
}) {
  return {
    kind: 'sc-upload-success',
    title: fileCount === 1 ? 'Document received' : 'Documents received',
    type: 'success',
    fileCount,
    uploadedAtLabel: formatUploadDateTime(uploadedAt),
  };
}

// FineUploaderBasic callbacks driving the upload lifecycle. Extracted so tests
// can assert dispatches without mocking the dynamically-imported uploader.
export function buildClaimsEvidenceCallbacks(
  dispatch,
  {
    appealId,
    totalFiles,
    totalSize,
    fileMetadataArray,
    filesWithRetryInfo = [],
    retryFileCount = 0,
    batchId,
    batchStartedAt,
  },
) {
  let filesComplete = 0;
  let successFileCount = 0;
  let bytesComplete = 0;
  let hasError = false;
  // Accumulated here rather than read back from the uploads reducer: redux
  // `failedFiles` is cleared and rewritten by other actions in this flow, and
  // the analytics counts have to reflect this batch only.
  const errorFiles = [];
  // Per-file state, indexed by the same FineUploader id that indexes
  // fileMetadataArray. Both are read in onComplete, which emits the one per-file
  // row. Fresh per submit, so neither needs cleanup.
  const startedAt = []; // id -> ms timestamp when the request began
  const rejection = []; // id -> { errorKey, status }, refused files only

  const docTypeFor = id =>
    DOC_TYPE_MAP.get(fileMetadataArray[id]?.docType?.value) || null;

  // No start time means no duration — the key is left off rather than reporting
  // one measured from nothing.
  const batchDurationMs = () =>
    batchStartedAt === undefined ? undefined : Date.now() - batchStartedAt;

  return {
    onAllComplete: () => {
      dispatch({ type: DONE_UPLOADING });

      if (!hasError) {
        recordUploadSuccessEvent({
          fileCount: successFileCount,
          retryFileCount,
          batchId,
          durationMs: batchDurationMs(),
          apiName: CLAIMS_EVIDENCE_API_NAME,
        });
        dispatch(clearSCUploadErrors());
        dispatch(
          setNotification(
            buildSupplementalClaimUploadNotification({
              fileCount: successFileCount,
              uploadedAt: new Date(),
            }),
          ),
        );
      } else {
        recordUploadFailureEvent({
          errorFiles,
          files: fileMetadataArray,
          filesWithRetryInfo,
          claimId: appealId,
          retryFileCount,
          successFileCount,
          batchId,
          durationMs: batchDurationMs(),
          apiName: CLAIMS_EVIDENCE_API_NAME,
        });
      }

      // Re-fetch appeals so evidenceSubmissions reflects the new uploads.
      if (successFileCount > 0) {
        dispatch(getAppealsV2());
      }
    },
    onTotalProgress: bytes => {
      bytesComplete = bytes;
      dispatch({
        type: SET_PROGRESS,
        progress: calcProgress(
          totalFiles,
          totalSize,
          filesComplete,
          bytesComplete,
        ),
      });
    },
    // Stamps the file's request start so onComplete can report its duration.
    // fine-uploader sends 3 files at a time (default maxConnections), so files
    // past the first three start later and check the token again. It waits on
    // the returned promise before sending the file.
    onUpload: id => {
      startedAt[id] = Date.now();
      return refreshAccessTokenBeforeUpload();
    },
    // `uuid` is present only when Claims Evidence accepted the file. We count it
    // here rather than taking it from FineUploader's `successful` list, which is read
    // inside a setTimeout and emptied after the first call, so it can arrive empty.
    // A cancelled file never completes, so it is correctly excluded.
    //
    // Also the single per-file analytics emission: onError has already run for a
    // rejected file, so the outcome, duration and rejection are all known here.
    onComplete: (id, fileName, responseJSON) => {
      const succeeded = Boolean(responseJSON?.uuid);

      if (succeeded) {
        successFileCount += 1;
      }

      const fileStartedAt = startedAt[id];
      recordFileUploadEvent({
        succeeded,
        // Absent rather than 0 when the file never started uploading.
        durationMs:
          fileStartedAt === undefined ? undefined : Date.now() - fileStartedAt,
        ...rejection[id],
        docType: docTypeFor(id),
        file: fileMetadataArray[id]?.file,
        batchId,
        apiName: CLAIMS_EVIDENCE_API_NAME,
      });

      filesComplete += 1;
      dispatch({
        type: SET_PROGRESS,
        progress: calcProgress(
          totalFiles,
          totalSize,
          filesComplete,
          bytesComplete,
        ),
      });
    },
    // Fires from inside fine-uploader's own completion handling, so onComplete
    // always follows for a rejected file.
    //
    // The xhr is defaulted because two of its onError call sites pass none —
    // `_batchError` and its own validation — and destructuring undefined would
    // throw. Both also pass a null id, so the status guards record nothing.
    onError: (id, fileName, _reason, { response, status } = {}) => {
      if (status === 401 || status === 403) {
        dispatch({ type: SET_UNAUTHORIZED });
      }

      if (status < 200 || status > 299) {
        const docType = docTypeFor(id);
        const errorKey = parseUploadErrorKey(response);

        errorFiles.push({
          errors: [{ detail: errorKey }],
          docType,
          fileIndex: id,
        });

        // Held for onComplete rather than reported here, so one row per file
        // carries the reason and the timing together.
        rejection[id] = { errorKey, status };

        dispatch({
          type: SET_UPLOAD_ERROR,
          payload: { fileName, fileType: docType },
        });

        hasError = true;
      }
    },
  };
}

// Supplemental claim evidence upload — synchronous backend (POST /v0/claims_evidence).
// Uploads files in parallel via FineUploaderBasic (default 3 connections), one file
// per request. On completion, re-fetches /appeals so the new evidenceSubmissions
// appear in the files-received view without a manual refresh.
export function submitClaimsEvidenceFiles({ appealId, files, batchId }) {
  const totalSize = files.reduce((sum, file) => sum + file.file.size, 0);
  const totalFiles = files.length;

  return (dispatch, getState) => {
    // Started before the fine-uploader chunk is imported, so the duration covers
    // everything the veteran waits through after pressing submit.
    const batchStartedAt = Date.now();

    // Inside the thunk, not the action creator: this writes the retry
    // fingerprints to sessionStorage, so constructing the action shouldn't
    // trigger it.
    const { filesWithRetryInfo, retryFileCount } = recordUploadStartEvent({
      files,
      claimId: appealId,
      batchId,
      apiName: CLAIMS_EVIDENCE_API_NAME,
    });

    dispatch(clearNotification());
    dispatch({ type: SET_UPLOADING, uploading: true });
    dispatch({ type: SET_PROGRESS, progress: 0 });

    return loadUploaderAfterTokenRefresh(getState).then(FineUploaderBasic => {
      if (!FineUploaderBasic) return;

      const csrfTokenStored = localStorage.getItem('csrfToken');

      const uploader = new FineUploaderBasic({
        request: {
          endpoint: `${environment.API_URL}/v0/claims_evidence`,
          inputName: 'file',
          customHeaders: {
            'Source-App-Name': manifest.entryName,
            'X-Key-Inflection': 'camel',
            'X-CSRF-Token': csrfTokenStored,
          },
        },
        cors: {
          expected: true,
          sendCredentials: true,
        },
        multiple: false,
        callbacks: buildClaimsEvidenceCallbacks(dispatch, {
          appealId,
          totalFiles,
          totalSize,
          fileMetadataArray: files,
          filesWithRetryInfo,
          retryFileCount,
          batchId,
          batchStartedAt,
        }),
      });

      dispatch({ type: SET_UPLOADER, uploader });

      files.forEach(fileMeta => {
        uploader.addFiles(
          fileMeta.file,
          buildClaimsEvidenceUploadParams(fileMeta, appealId),
        );
      });
    });
  };
}
